import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Settings, Database, Zap, Shield, CheckCircle, ChevronDown,
  ChevronRight, AlertTriangle, Users, ArrowRight,
  Layers, GitBranch, Bell, RefreshCw, Lock, Info,
} from 'lucide-react';
import { getAllStages, getCRMStages } from '../../lib/leadPipelineRegistry';

// ─── Static data (derived from content/lead-pipeline.md) ──────────────────

const STAGE_AUTOMATIONS: Record<string, { name: string; nameAr: string; category: string }[]> = {
  'pre_stage': [
    { name: 'Auto Lead Creation', nameAr: 'إنشاء عميل تلقائي', category: 'ingestion' },
    { name: 'De-duplication Engine', nameAr: 'محرك إزالة التكرار', category: 'quality' },
    { name: 'Auto-Assignment (round-robin)', nameAr: 'التعيين التلقائي (دوري)', category: 'assignment' },
    { name: 'Auto-Task Creation (5-min SLA)', nameAr: 'إنشاء مهمة تلقائية (5 دقائق)', category: 'sla' },
    { name: 'Welcome WhatsApp Message', nameAr: 'رسالة ترحيب عبر واتساب', category: 'communication' },
    { name: 'Data Enrichment (returning customer)', nameAr: 'إثراء البيانات (عميل عائد)', category: 'quality' },
  ],
  'new_unworked': [
    { name: 'SLA Timer (countdown)', nameAr: 'مؤقت الاتفاقية (عد تنازلي)', category: 'sla' },
    { name: 'SLA Breach Alert at 80%', nameAr: 'تنبيه خرق الاتفاقية عند 80%', category: 'sla' },
    { name: 'SLA Breach Escalation at 100%', nameAr: 'تصعيد خرق الاتفاقية عند 100%', category: 'sla' },
    { name: 'Auto-Reassign (3-min no open)', nameAr: 'إعادة تعيين تلقائية (3 دقائق)', category: 'assignment' },
    { name: 'WhatsApp Auto-Send on No Answer', nameAr: 'إرسال واتساب تلقائي عند عدم الرد', category: 'communication' },
    { name: 'Callback Detection & Alert', nameAr: 'كشف المعاودة والتنبيه', category: 'detection' },
  ],
  'contacting': [
    { name: 'Cadence Task Creation', nameAr: 'إنشاء مهام الجدول الزمني', category: 'cadence' },
    { name: 'Time-Based Triggers (next-day)', nameAr: 'محفزات زمنية (اليوم التالي)', category: 'cadence' },
    { name: 'Cadence Completion Check (attempt 8)', nameAr: 'فحص اكتمال الجدول (المحاولة 8)', category: 'cadence' },
    { name: 'Response Detection & Alert', nameAr: 'كشف الاستجابة والتنبيه', category: 'detection' },
    { name: 'After-Hours Auto-Reply', nameAr: 'رد تلقائي خارج ساعات العمل', category: 'communication' },
    { name: 'Stale Lead Alert (8h no attempt)', nameAr: 'تنبيه العميل الراكد (8 ساعات)', category: 'sla' },
    { name: 'Auto-Escalate to Manager (72h)', nameAr: 'تصعيد تلقائي للمدير (72 ساعة)', category: 'sla' },
  ],
  'contacted': [
    { name: 'Lead Scoring (timeline+budget+trade)', nameAr: 'تسجيل النقاط (موعد+ميزانية+تبادل)', category: 'scoring' },
    { name: 'Qualification Timer (<45 sec alert)', nameAr: 'مؤقت التأهيل (تنبيه <45 ثانية)', category: 'quality' },
    { name: 'Auto-Suggest Branch (by city)', nameAr: 'اقتراح الفرع تلقائيًا (حسب المدينة)', category: 'assignment' },
    { name: 'Finance Pre-Check Trigger', nameAr: 'محفز الفحص المالي المسبق', category: 'finance' },
    { name: 'Nurture Campaign Enrollment', nameAr: 'التسجيل في حملة التغذية', category: 'marketing' },
    { name: 'Appointment Slot Suggestion', nameAr: 'اقتراح موعد للتعيين', category: 'scheduling' },
  ],
  'qualified': [
    { name: 'Slot Availability Check (real-time)', nameAr: 'فحص توفر المواعيد (فوري)', category: 'scheduling' },
    { name: 'Smart Advisor Matching', nameAr: 'مطابقة المستشار الذكية', category: 'assignment' },
    { name: 'Auto-Assignment on Confirmation', nameAr: 'التعيين التلقائي عند التأكيد', category: 'assignment' },
    { name: 'Confirmation Messages (WA+SMS)', nameAr: 'رسائل التأكيد (واتساب+SMS)', category: 'communication' },
    { name: 'Calendar Sync (advisor)', nameAr: 'مزامنة التقويم (المستشار)', category: 'scheduling' },
    { name: 'Preparation Checklist Trigger', nameAr: 'محفز قائمة التحضير', category: 'quality' },
    { name: 'Stale Alert (>4h no appointment)', nameAr: 'تنبيه الركود (>4 ساعات)', category: 'sla' },
  ],
  'appointment_scheduled': [
    { name: '24h Reminder (WhatsApp)', nameAr: 'تذكير 24 ساعة (واتساب)', category: 'communication' },
    { name: '3h Reminder (WhatsApp)', nameAr: 'تذكير 3 ساعات (واتساب)', category: 'communication' },
    { name: 'Advisor Alert on Confirmation', nameAr: 'تنبيه المستشار عند التأكيد', category: 'communication' },
    { name: 'No-Show Detection (15 min after)', nameAr: 'كشف عدم الحضور (15 دقيقة بعد)', category: 'detection' },
    { name: 'No-Show Recovery Task', nameAr: 'مهمة استرداد عدم الحضور', category: 'recovery' },
    { name: 'Preparation Checklist (auto)', nameAr: 'قائمة التحضير التلقائية', category: 'quality' },
    { name: 'Reschedule Workflow (via WA)', nameAr: 'سير عمل إعادة الجدولة (واتساب)', category: 'scheduling' },
    { name: 'Calendar Integration & Conflict Alert', nameAr: 'تكامل التقويم وتنبيه التعارض', category: 'scheduling' },
  ],
  'showroom_engagement': [
    { name: 'Check-in Alert (advisor notify)', nameAr: 'تنبيه تسجيل الوصول', category: 'communication' },
    { name: 'Test Drive Assignment (Captain)', nameAr: 'تعيين اختبار القيادة (الكابتن)', category: 'assignment' },
    { name: 'Portal Integration (registration)', nameAr: 'تكامل البوابة (التسجيل)', category: 'integration' },
    { name: 'Timer Tracking (>2h alert)', nameAr: 'تتبع الوقت (تنبيه >2 ساعة)', category: 'sla' },
    { name: 'Trade-in Photo Upload Link', nameAr: 'رابط رفع صور التبادل', category: 'quality' },
    { name: 'Finance Pre-Check Trigger', nameAr: 'محفز الفحص المالي المسبق', category: 'finance' },
    { name: 'Manager Alert (>500K SAR / heavy negotiation)', nameAr: 'تنبيه المدير (>500K ريال)', category: 'escalation' },
    { name: 'Outcome Required Gate', nameAr: 'بوابة النتيجة المطلوبة', category: 'quality' },
  ],
  'quote_sent': [
    { name: 'Quote Generation (auto-populate)', nameAr: 'إنشاء العرض (ملء تلقائي)', category: 'documents' },
    { name: 'Approval Routing (discount > threshold)', nameAr: 'توجيه الموافقة (خصم > الحد)', category: 'approval' },
    { name: 'Delivery Notification (PDF ready)', nameAr: 'إشعار التسليم (PDF جاهز)', category: 'communication' },
    { name: 'Receipt Tracking (WA read)', nameAr: 'تتبع الاستلام (قراءة واتساب)', category: 'detection' },
    { name: 'Follow-up Tasks (per cadence)', nameAr: 'مهام المتابعة (حسب الجدول)', category: 'cadence' },
    { name: 'Expiry Alert (24h before)', nameAr: 'تنبيه انتهاء الصلاحية (24 ساعة)', category: 'sla' },
    { name: 'Stale Quote Alert (>3 days)', nameAr: 'تنبيه العرض الراكد (>3 أيام)', category: 'sla' },
    { name: 'Competitive Alert (competitor mention)', nameAr: 'تنبيه المنافس (ذكر المنافس)', category: 'detection' },
  ],
  'negotiation_finance_tradein': [
    { name: 'Finance Status Auto-Update (lender)', nameAr: 'تحديث حالة التمويل التلقائي', category: 'finance' },
    { name: 'Document Checklist (auto-generate)', nameAr: 'قائمة المستندات (إنشاء تلقائي)', category: 'documents' },
    { name: 'Trade-in Valuation Integration', nameAr: 'تكامل تقييم التبادل', category: 'integration' },
    { name: 'Aging Alerts (>5 days no progress)', nameAr: 'تنبيهات الشيخوخة (>5 أيام)', category: 'sla' },
    { name: 'Daily Deal Review (manager list)', nameAr: 'مراجعة الصفقة اليومية (قائمة المدير)', category: 'reporting' },
    { name: 'Approval Workflow (discount/exception)', nameAr: 'سير عمل الموافقة (خصم/استثناء)', category: 'approval' },
    { name: 'Lender Submission Tracking', nameAr: 'تتبع تقديم المُقرض', category: 'finance' },
    { name: 'Customer Comms Log (auto-log)', nameAr: 'سجل اتصالات العميل (تلقائي)', category: 'quality' },
  ],
  'booked_contracting': [
    { name: 'Contract Generation (auto-populate)', nameAr: 'إنشاء العقد (ملء تلقائي)', category: 'documents' },
    { name: 'Payment Tracking (deposit integration)', nameAr: 'تتبع الدفع (تكامل الإيداع)', category: 'finance' },
    { name: 'VIN Allocation System', nameAr: 'نظام تخصيص رقم VIN', category: 'inventory' },
    { name: 'Delivery Calendar Slot Check', nameAr: 'فحص موعد التسليم', category: 'scheduling' },
    { name: 'Document Checklist Tracker', nameAr: 'متتبع قائمة المستندات', category: 'documents' },
    { name: 'Delivery Coordinator Notification', nameAr: 'إشعار منسق التسليم', category: 'communication' },
    { name: 'Customer Booking Confirmation', nameAr: 'تأكيد حجز العميل', category: 'communication' },
    { name: 'Trade-in Paperwork Workflow', nameAr: 'سير عمل أوراق التبادل', category: 'documents' },
    { name: 'Finance System Sync', nameAr: 'مزامنة نظام التمويل', category: 'integration' },
  ],
  'delivery_post_sale': [
    { name: 'PDI Checklist (auto-generate)', nameAr: 'قائمة PDI (إنشاء تلقائي)', category: 'quality' },
    { name: 'Delivery Countdown Reminders (3d, 1d)', nameAr: 'تذكيرات العد التنازلي (3أيام، 1يوم)', category: 'communication' },
    { name: 'Day 2/7/30/90 Follow-up Tasks', nameAr: 'مهام المتابعة (يوم 2/7/30/90)', category: 'follow_up' },
    { name: 'CSAT/NPS Survey Automation (Day 30)', nameAr: 'أتمتة استطلاع CSAT/NPS (اليوم 30)', category: 'survey' },
    { name: 'First Service Reminder (auto-schedule)', nameAr: 'تذكير الخدمة الأولى (تلقائي)', category: 'follow_up' },
  ],
};

const VALIDATION_RULES: Record<string, { rule: string; ruleAr: string; severity: 'block' | 'warn' | 'info' }[]> = {
  'pre_stage': [
    { rule: 'Mobile must be unique OR linked to existing record', ruleAr: 'الجوال يجب أن يكون فريدًا أو مرتبطًا', severity: 'block' },
    { rule: 'Source channel from approved picklist only', ruleAr: 'قناة المصدر من القائمة المعتمدة فقط', severity: 'block' },
    { rule: 'Brand interest required before assignment', ruleAr: 'الاهتمام بالعلامة التجارية مطلوب', severity: 'block' },
    { rule: 'Walk-in source requires branch specification', ruleAr: 'المصدر المباشر يتطلب تحديد الفرع', severity: 'block' },
    { rule: 'OEM portal dealer code must match Almajdouie', ruleAr: 'كود الوكيل يجب أن يطابق المجدوعي', severity: 'block' },
  ],
  'new_unworked': [
    { rule: 'Cannot save without Next Action Type + DateTime', ruleAr: 'لا يمكن الحفظ بدون نوع الإجراء التالي + التاريخ', severity: 'block' },
    { rule: 'First contact datetime must be captured for SLA', ruleAr: 'تاريخ أول اتصال مطلوب للاتفاقية', severity: 'block' },
    { rule: 'Outcome required before stage exit', ruleAr: 'النتيجة مطلوبة قبل الخروج من المرحلة', severity: 'block' },
    { rule: 'WhatsApp channel: message_sent_datetime required', ruleAr: 'قناة واتساب: تاريخ الإرسال مطلوب', severity: 'warn' },
  ],
  'contacting': [
    { rule: 'Cannot mark Unreachable until 8 attempts logged', ruleAr: 'لا يمكن وضع "لا يمكن الوصول" قبل 8 محاولات', severity: 'block' },
    { rule: 'Next attempt must be within cadence timeline', ruleAr: 'المحاولة التالية يجب أن تكون ضمن الجدول', severity: 'block' },
    { rule: 'At least 2 different channels per cadence', ruleAr: 'على الأقل قناتان مختلفتان في كل جدول', severity: 'block' },
    { rule: 'Attempts spread across BDC hours (08:00-17:00)', ruleAr: 'المحاولات موزعة خلال ساعات BDC', severity: 'warn' },
    { rule: 'Active leads must always have next action', ruleAr: 'العملاء النشطون يجب أن يكون لديهم إجراء تالٍ', severity: 'block' },
  ],
  'qualified': [
    { rule: 'Cannot move to Stage 5 without appointment_datetime', ruleAr: 'لا يمكن الانتقال لمرحلة 5 بدون تاريخ الموعد', severity: 'block' },
    { rule: 'assigned_advisor must be set and active', ruleAr: 'المستشار المعين يجب أن يكون نشطًا', severity: 'block' },
    { rule: 'confirmation_sent must be true', ruleAr: 'التأكيد يجب أن يكون مرسلًا', severity: 'block' },
    { rule: 'appointment_datetime must be in future', ruleAr: 'تاريخ الموعد يجب أن يكون في المستقبل', severity: 'block' },
    { rule: 'Advisor must belong to appointment_branch', ruleAr: 'المستشار يجب أن ينتمي لفرع الموعد', severity: 'block' },
  ],
  'booked_contracting': [
    { rule: 'deposit_amount must be > 0', ruleAr: 'مبلغ الإيداع يجب أن يكون > 0', severity: 'block' },
    { rule: 'contract_signed_datetime must be populated', ruleAr: 'تاريخ توقيع العقد مطلوب', severity: 'block' },
    { rule: 'delivery_scheduled_datetime required', ruleAr: 'تاريخ التسليم المجدول مطلوب', severity: 'block' },
    { rule: 'documents_complete must be true', ruleAr: 'المستندات يجب أن تكون مكتملة', severity: 'block' },
    { rule: 'finance_contract_signed if finance_required', ruleAr: 'عقد التمويل مطلوب إذا كان التمويل مطلوبًا', severity: 'block' },
    { rule: 'delivery_coordinator must be assigned', ruleAr: 'منسق التسليم يجب أن يكون معينًا', severity: 'block' },
  ],
};

const INTEGRATIONS = [
  { id: 'whatsapp', name: 'WhatsApp Business API', nameAr: 'واجهة برمجة واتساب للأعمال', type: 'Communication', typeAr: 'التواصل', status: 'required', icon: '💬', desc: 'Outbound templates, inbound message routing, read-receipt tracking', descAr: 'قوالب صادرة، توجيه الرسائل الواردة، تتبع الاستلام' },
  { id: 'telephony', name: 'Telephony / Call Center', nameAr: 'نظام الهاتف / مركز الاتصال', type: 'Communication', typeAr: 'التواصل', status: 'required', icon: '📞', desc: 'Call logging, outcome capture, callback detection, IVR integration', descAr: 'تسجيل المكالمات، التقاط النتيجة، كشف المعاودة، تكامل IVR' },
  { id: 'oem_portal', name: 'OEM Portal (per brand)', nameAr: 'بوابة OEM (لكل علامة تجارية)', type: 'Lead Source', typeAr: 'مصدر العملاء', status: 'required', icon: '🚗', desc: 'Real-time lead ingestion via webhook; dealer code validation', descAr: 'استيعاب العملاء الفوري عبر webhook؛ التحقق من كود الوكيل' },
  { id: 'website', name: 'Website / Landing Pages', nameAr: 'الموقع الإلكتروني / صفحات الهبوط', type: 'Lead Source', typeAr: 'مصدر العملاء', status: 'required', icon: '🌐', desc: 'Form submissions, UTM parameter capture, WhatsApp consent checkbox', descAr: 'تقديم النماذج، التقاط معاملات UTM، خانة موافقة واتساب' },
  { id: 'finance', name: 'Finance / Leasing System', nameAr: 'نظام التمويل / الإيجار', type: 'Finance', typeAr: 'التمويل', status: 'required', icon: '🏦', desc: 'Pre-approval submission, status polling, contract sync', descAr: 'تقديم الموافقة المسبقة، استطلاع الحالة، مزامنة العقد' },
  { id: 'inventory', name: 'Inventory Management', nameAr: 'إدارة المخزون', type: 'Operations', typeAr: 'العمليات', status: 'required', icon: '🏭', desc: 'VIN allocation, vehicle availability, spec matching for quotes', descAr: 'تخصيص VIN، توفر المركبات، مطابقة المواصفات للعروض' },
  { id: 'payment', name: 'Payment Gateway', nameAr: 'بوابة الدفع', type: 'Finance', typeAr: 'التمويل', status: 'required', icon: '💳', desc: 'Deposit payment confirmation, installment tracking', descAr: 'تأكيد دفع الإيداع، تتبع الأقساط' },
  { id: 'tradein', name: 'Trade-in Valuation Platform', nameAr: 'منصة تقييم التبادل', type: 'Operations', typeAr: 'العمليات', status: 'optional', icon: '🔄', desc: 'Auto-fetch market valuation; photo upload link; approval workflow', descAr: 'جلب تلقائي لتقييم السوق؛ رابط رفع الصور؛ سير عمل الموافقة' },
  { id: 'social', name: 'Social Media / Marketplace APIs', nameAr: 'واجهات وسائل التواصل / الأسواق', type: 'Lead Source', typeAr: 'مصدر العملاء', status: 'required', icon: '📱', desc: 'Facebook/Instagram lead ads, Syarah/Motory marketplace webhook', descAr: 'إعلانات العملاء فيسبوك/إنستقرام، webhook أسواق سيارة/موتوري' },
  { id: 'calendar', name: 'Calendar System (Outlook/Google)', nameAr: 'نظام التقويم (Outlook/Google)', type: 'Scheduling', typeAr: 'الجدولة', status: 'required', icon: '📅', desc: 'Advisor calendar sync, appointment blocking, conflict detection', descAr: 'مزامنة تقويم المستشار، حجب المواعيد، كشف التعارض' },
  { id: 'survey', name: 'Survey / CSAT Platform', nameAr: 'منصة الاستطلاع / CSAT', type: 'Post-Sale', typeAr: 'ما بعد البيع', status: 'optional', icon: '⭐', desc: 'Day 30 CSAT and NPS survey dispatch, response capture back to CRM', descAr: 'إرسال استطلاع CSAT وNPS اليوم 30، التقاط الردود في CRM' },
  { id: 'sms', name: 'SMS Gateway', nameAr: 'بوابة الرسائل القصيرة', type: 'Communication', typeAr: 'التواصل', status: 'required', icon: '📨', desc: 'Fallback channel for reminders and cadence attempt 8', descAr: 'قناة احتياطية للتذكيرات والمحاولة الثامنة' },
];

const CATEGORY_COLORS: Record<string, string> = {
  ingestion: 'bg-blue-100 text-blue-700',
  quality: 'bg-green-100 text-green-700',
  assignment: 'bg-purple-100 text-purple-700',
  sla: 'bg-red-100 text-red-700',
  communication: 'bg-teal-100 text-teal-700',
  detection: 'bg-yellow-100 text-yellow-700',
  cadence: 'bg-orange-100 text-orange-700',
  scoring: 'bg-indigo-100 text-indigo-700',
  finance: 'bg-emerald-100 text-emerald-700',
  marketing: 'bg-pink-100 text-pink-700',
  scheduling: 'bg-sky-100 text-sky-700',
  documents: 'bg-amber-100 text-amber-700',
  approval: 'bg-rose-100 text-rose-700',
  recovery: 'bg-lime-100 text-lime-700',
  integration: 'bg-violet-100 text-violet-700',
  inventory: 'bg-cyan-100 text-cyan-700',
  reporting: 'bg-slate-100 text-slate-700',
  escalation: 'bg-red-100 text-red-700',
  follow_up: 'bg-blue-100 text-blue-700',
  survey: 'bg-green-100 text-green-700',
};

const SEVERITY_META = {
  block: { label: 'Blocks Save', labelAr: 'يمنع الحفظ', color: 'bg-red-100 text-red-700', icon: <Lock className="w-3 h-3" /> },
  warn: { label: 'Warning', labelAr: 'تحذير', color: 'bg-yellow-100 text-yellow-700', icon: <AlertTriangle className="w-3 h-3" /> },
  info: { label: 'Info', labelAr: 'معلومات', color: 'bg-blue-100 text-blue-700', icon: <Info className="w-3 h-3" /> },
};

// ─── Sub-sections ──────────────────────────────────────────────────────────

type ActiveSection = 'overview' | 'stagemap' | 'automations' | 'fields' | 'validation' | 'integrations';

// Stage Map Tab
function StageMapTab({ isAr }: { isAr: boolean }) {
  const stages = getAllStages();

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          {isAr
            ? 'خريطة مراحل CRM تُظهر تسلسل المراحل، المالك، الاتفاقية، وعدد الحقول الإلزامية لكل مرحلة.'
            : 'The CRM Stage Map defines the sequential pipeline, stage owner, SLA, and mandatory field count for each stage.'}
        </div>
      </div>

      {/* Pipeline Flow */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <h3 className="text-base font-bold text-gray-900 mb-5 flex items-center gap-2">
          <GitBranch className="w-5 h-5 text-blue-600" />
          {isAr ? 'تدفق مراحل CRM' : 'CRM Pipeline Stage Flow'}
        </h3>
        <div className="space-y-2">
          {stages.map((stage, idx) => (
            <div key={stage.meta.id}>
              <div className="flex items-center gap-3 p-4 rounded-xl border border-gray-200 bg-gray-50 hover:bg-white hover:shadow-sm transition-all group">
                {/* Order Badge */}
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
                  style={{ backgroundColor: stage.meta.color || '#6366f1' }}
                >
                  {stage.meta.order === 0 ? 'P' : stage.meta.order}
                </div>

                {/* Stage Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900">
                      {isAr ? stage.meta.titleAr : stage.meta.title}
                    </span>
                    <span className="text-xs text-gray-400 font-mono">{stage.meta.id}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {isAr ? `المالك: ${stage.meta.ownerAr}` : `Owner: ${stage.meta.owner}`}
                  </div>
                </div>

                {/* SLA */}
                <div className="hidden sm:block text-right flex-shrink-0">
                  <div className="text-xs font-semibold text-teal-700 bg-teal-50 px-2 py-1 rounded-lg">
                    {isAr ? stage.meta.slaAr : stage.meta.sla}
                  </div>
                </div>

                {/* Fields count */}
                <div className="hidden md:block text-right flex-shrink-0 ml-2">
                  <div className="text-xs text-gray-500">
                    <span className="font-bold text-gray-800">{stage.mandatoryFields?.length ?? 0}</span> {isAr ? 'حقل' : 'fields'}
                  </div>
                </div>

                {/* Link */}
                <Link to={`/pipeline/stage/${stage.meta.id}`} className="ml-2 flex-shrink-0">
                  <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600" />
                </Link>
              </div>
              {idx < stages.length - 1 && (
                <div className="flex justify-center my-1">
                  <div className="w-0.5 h-4 bg-gray-200" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* CRM Field Summary Table */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Database className="w-5 h-5 text-blue-600" />
          {isAr ? 'ملخص الحقول الإلزامية حسب المرحلة' : 'Mandatory Fields Count by Stage'}
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 font-semibold text-gray-700">{isAr ? 'المرحلة' : 'Stage'}</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">{isAr ? 'الاتفاقية' : 'SLA'}</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-700">{isAr ? 'حقول إلزامية' : 'Mandatory Fields'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {stages.map((stage) => (
                <tr key={stage.meta.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span
                        className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white"
                        style={{ backgroundColor: stage.meta.color || '#6366f1' }}
                      >
                        {stage.meta.order === 0 ? 'P' : stage.meta.order}
                      </span>
                      <span className="font-medium text-gray-800">{isAr ? stage.meta.titleAr : stage.meta.title}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-teal-700 font-semibold">{isAr ? stage.meta.slaAr : stage.meta.sla}</td>
                  <td className="px-4 py-3 text-right">
                    <span className="inline-block bg-blue-100 text-blue-700 text-xs font-bold px-2 py-0.5 rounded-full">
                      {stage.mandatoryFields?.length ?? 0}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Automations Tab
function AutomationsTab({ isAr }: { isAr: boolean }) {
  const stages = getAllStages();
  const [expandedStage, setExpandedStage] = useState<string | null>('new_unworked');
  const [filterCategory, setFilterCategory] = useState<string>('all');

  const allCategories = Array.from(
    new Set(Object.values(STAGE_AUTOMATIONS).flatMap(autos => autos.map(a => a.category)))
  ).sort();

  const totalAutomations = Object.values(STAGE_AUTOMATIONS).reduce((sum, a) => sum + a.length, 0);

  // Stage id mapping from pipeline meta id to STAGE_AUTOMATIONS key
  const stageKeyMap: Record<string, string> = {
    'pre_stage': 'pre_stage',
    'new_unworked': 'new_unworked',
    'contacting': 'contacting',
    'contacted': 'contacted',
    'qualified': 'qualified',
    'appointment_scheduled': 'appointment_scheduled',
    'showroom_engagement': 'showroom_engagement',
    'quote_sent': 'quote_sent',
    'negotiation_finance_tradein': 'negotiation_finance_tradein',
    'booked_contracting': 'booked_contracting',
    'delivery_post_sale': 'delivery_post_sale',
  };

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center shadow-sm">
          <Zap className="w-5 h-5 text-orange-500 mx-auto mb-1" />
          <div className="text-2xl font-bold text-gray-900">{totalAutomations}</div>
          <div className="text-xs text-gray-500">{isAr ? 'إجمالي الأتمتة' : 'Total Automations'}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center shadow-sm">
          <Layers className="w-5 h-5 text-blue-500 mx-auto mb-1" />
          <div className="text-2xl font-bold text-gray-900">{Object.keys(STAGE_AUTOMATIONS).length}</div>
          <div className="text-xs text-gray-500">{isAr ? 'مراحل بأتمتة' : 'Stages with Automations'}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center shadow-sm">
          <Bell className="w-5 h-5 text-red-500 mx-auto mb-1" />
          <div className="text-2xl font-bold text-gray-900">
            {Object.values(STAGE_AUTOMATIONS).flatMap(a => a).filter(a => a.category === 'sla').length}
          </div>
          <div className="text-xs text-gray-500">{isAr ? 'أتمتة الاتفاقية' : 'SLA Automations'}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center shadow-sm">
          <RefreshCw className="w-5 h-5 text-green-500 mx-auto mb-1" />
          <div className="text-2xl font-bold text-gray-900">
            {Object.values(STAGE_AUTOMATIONS).flatMap(a => a).filter(a => a.category === 'communication').length}
          </div>
          <div className="text-xs text-gray-500">{isAr ? 'أتمتة التواصل' : 'Comms Automations'}</div>
        </div>
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setFilterCategory('all')}
          className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${filterCategory === 'all' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
        >
          {isAr ? 'الكل' : 'All'}
        </button>
        {allCategories.map(cat => (
          <button
            key={cat}
            onClick={() => setFilterCategory(cat)}
            className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors capitalize ${filterCategory === cat ? `${CATEGORY_COLORS[cat] || 'bg-gray-100 text-gray-600'} ring-2 ring-offset-1 ring-gray-300` : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            {cat.replace(/_/g, ' ')}
          </button>
        ))}
      </div>

      {/* Per-stage accordion */}
      <div className="space-y-2">
        {stages.map((stage) => {
          const key = stageKeyMap[stage.meta.id];
          const autos = key ? (STAGE_AUTOMATIONS[key] || []) : [];
          const filtered = filterCategory === 'all' ? autos : autos.filter(a => a.category === filterCategory);
          if (filtered.length === 0) return null;
          const isOpen = expandedStage === stage.meta.id;

          return (
            <div key={stage.meta.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
              <button
                onClick={() => setExpandedStage(isOpen ? null : stage.meta.id)}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                    style={{ backgroundColor: stage.meta.color || '#6366f1' }}
                  >
                    {stage.meta.order === 0 ? 'P' : stage.meta.order}
                  </div>
                  <div className="text-left">
                    <span className="font-semibold text-gray-800">{isAr ? stage.meta.titleAr : stage.meta.title}</span>
                    <span className="ml-2 text-xs text-gray-400">({filtered.length} {isAr ? 'أتمتة' : 'automations'})</span>
                  </div>
                </div>
                {isOpen ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
              </button>
              {isOpen && (
                <div className="px-5 pb-4 border-t border-gray-100">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                    {filtered.map((auto, i) => (
                      <div key={i} className="flex items-start gap-2.5 p-3 rounded-lg bg-gray-50 border border-gray-100">
                        <Zap className="w-3.5 h-3.5 text-orange-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-gray-800 font-medium leading-tight">{isAr ? auto.nameAr : auto.name}</div>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${CATEGORY_COLORS[auto.category] || 'bg-gray-100 text-gray-600'}`}>
                          {auto.category.replace(/_/g, ' ')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Validation Rules Tab
function ValidationTab({ isAr }: { isAr: boolean }) {
  const stages = getAllStages();
  const [expandedStage, setExpandedStage] = useState<string | null>('pre_stage');

  const stageKeyMap: Record<string, string> = {
    'pre_stage': 'pre_stage',
    'new_unworked': 'new_unworked',
    'contacting': 'contacting',
    'qualified': 'qualified',
    'booked_contracting': 'booked_contracting',
  };

  const totalRules = Object.values(VALIDATION_RULES).reduce((sum, r) => sum + r.length, 0);
  const blockRules = Object.values(VALIDATION_RULES).flatMap(r => r).filter(r => r.severity === 'block').length;

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center shadow-sm">
          <Shield className="w-5 h-5 text-blue-600 mx-auto mb-1" />
          <div className="text-2xl font-bold text-gray-900">{totalRules}</div>
          <div className="text-xs text-gray-500">{isAr ? 'إجمالي القواعد' : 'Total Rules'}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center shadow-sm">
          <Lock className="w-5 h-5 text-red-600 mx-auto mb-1" />
          <div className="text-2xl font-bold text-gray-900">{blockRules}</div>
          <div className="text-xs text-gray-500">{isAr ? 'قواعد حاجبة' : 'Blocking Rules'}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center shadow-sm">
          <AlertTriangle className="w-5 h-5 text-yellow-600 mx-auto mb-1" />
          <div className="text-2xl font-bold text-gray-900">{totalRules - blockRules}</div>
          <div className="text-xs text-gray-500">{isAr ? 'تحذيرات' : 'Warning Rules'}</div>
        </div>
      </div>

      {/* Per-stage rules */}
      <div className="space-y-2">
        {stages.map((stage) => {
          const key = stageKeyMap[stage.meta.id];
          const rules = key ? (VALIDATION_RULES[key] || []) : [];
          if (rules.length === 0) return null;
          const isOpen = expandedStage === key;

          return (
            <div key={stage.meta.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
              <button
                onClick={() => setExpandedStage(isOpen ? null : key!)}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                    style={{ backgroundColor: stage.meta.color || '#6366f1' }}
                  >
                    {stage.meta.order === 0 ? 'P' : stage.meta.order}
                  </div>
                  <div className="text-left">
                    <span className="font-semibold text-gray-800">{isAr ? stage.meta.titleAr : stage.meta.title}</span>
                    <span className="ml-2 text-xs text-gray-400">({rules.length} {isAr ? 'قواعد' : 'rules'})</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                    {rules.filter(r => r.severity === 'block').length} {isAr ? 'حاجب' : 'blocking'}
                  </span>
                  {isOpen ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                </div>
              </button>
              {isOpen && (
                <div className="px-5 pb-4 border-t border-gray-100">
                  <div className="space-y-2 mt-3">
                    {rules.map((rule, i) => {
                      const sev = SEVERITY_META[rule.severity];
                      return (
                        <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
                          <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 mt-0.5 ${sev.color}`}>
                            {sev.icon}
                            {isAr ? sev.labelAr : sev.label}
                          </span>
                          <span className="text-sm text-gray-700">{isAr ? rule.ruleAr : rule.rule}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-amber-800">
          {isAr
            ? 'هذه تغطي القواعد الأساسية للمراحل الرئيسية. المراحل الأخرى لديها قواعد مماثلة مضمنة في منطق CRM.'
            : 'These cover key stages. All other stages have equivalent validation rules embedded in CRM logic — view full stage detail for complete rules.'}
        </p>
      </div>
    </div>
  );
}

// Integrations Tab
function IntegrationsTab({ isAr }: { isAr: boolean }) {
  const typeColors: Record<string, string> = {
    'Communication': 'bg-blue-100 text-blue-700',
    'Lead Source': 'bg-green-100 text-green-700',
    'Finance': 'bg-yellow-100 text-yellow-700',
    'Operations': 'bg-purple-100 text-purple-700',
    'Scheduling': 'bg-teal-100 text-teal-700',
    'Post-Sale': 'bg-orange-100 text-orange-700',
  };

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center shadow-sm">
          <div className="text-2xl font-bold text-gray-900">{INTEGRATIONS.length}</div>
          <div className="text-xs text-gray-500">{isAr ? 'إجمالي التكاملات' : 'Total Integrations'}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center shadow-sm">
          <div className="text-2xl font-bold text-red-600">{INTEGRATIONS.filter(i => i.status === 'required').length}</div>
          <div className="text-xs text-gray-500">{isAr ? 'مطلوبة' : 'Required'}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center shadow-sm">
          <div className="text-2xl font-bold text-yellow-600">{INTEGRATIONS.filter(i => i.status === 'optional').length}</div>
          <div className="text-xs text-gray-500">{isAr ? 'اختيارية' : 'Optional'}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center shadow-sm">
          <div className="text-2xl font-bold text-blue-600">
            {Array.from(new Set(INTEGRATIONS.map(i => i.type))).length}
          </div>
          <div className="text-xs text-gray-500">{isAr ? 'فئات التكامل' : 'Integration Types'}</div>
        </div>
      </div>

      {/* Integration Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {INTEGRATIONS.map((integration) => (
          <div key={integration.id} className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-2xl">{integration.icon}</span>
                <div>
                  <h3 className="text-sm font-bold text-gray-800 leading-tight">
                    {isAr ? integration.nameAr : integration.name}
                  </h3>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${typeColors[integration.type] || 'bg-gray-100 text-gray-600'}`}>
                    {isAr ? integration.typeAr : integration.type}
                  </span>
                </div>
              </div>
              <span className={`text-xs font-bold px-2 py-1 rounded-full flex-shrink-0 ml-1 ${
                integration.status === 'required' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
              }`}>
                {integration.status === 'required' ? (isAr ? 'مطلوب' : 'Required') : (isAr ? 'اختياري' : 'Optional')}
              </span>
            </div>
            <p className="text-xs text-gray-600 leading-relaxed">
              {isAr ? integration.descAr : integration.desc}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

// Fields Tab
function FieldsTab({ isAr }: { isAr: boolean }) {
  const stages = getAllStages();
  const [selectedStage, setSelectedStage] = useState(stages[0]?.meta.id || '');

  const stage = stages.find(s => s.meta.id === selectedStage);
  const fields = stage?.mandatoryFields || [];

  return (
    <div className="space-y-4">
      {/* Stage Selector */}
      <div className="flex flex-wrap gap-2">
        {stages.map((s) => (
          <button
            key={s.meta.id}
            onClick={() => setSelectedStage(s.meta.id)}
            className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${
              selectedStage === s.meta.id
                ? 'text-white shadow-sm'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            style={selectedStage === s.meta.id ? { backgroundColor: s.meta.color || '#6366f1' } : {}}
          >
            <span>{s.meta.order === 0 ? 'P' : s.meta.order}</span>
            <span className="hidden sm:inline">{isAr ? s.meta.titleAr : s.meta.title}</span>
          </button>
        ))}
      </div>

      {/* Fields Table */}
      {stage && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between" style={{ backgroundColor: `${stage.meta.color}15` }}>
            <div className="flex items-center gap-2">
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white"
                style={{ backgroundColor: stage.meta.color || '#6366f1' }}
              >
                {stage.meta.order === 0 ? 'P' : stage.meta.order}
              </div>
              <h3 className="font-bold text-gray-900">{isAr ? stage.meta.titleAr : stage.meta.title}</h3>
              <span className="text-xs text-gray-500">— {fields.length} {isAr ? 'حقل إلزامي' : 'mandatory fields'}</span>
            </div>
            <Link to={`/pipeline/stage/${stage.meta.id}`} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
              {isAr ? 'عرض المرحلة' : 'View Stage'} <ArrowRight className="w-3 h-3" />
            </Link>
          </div>

          {fields.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left px-5 py-3 font-semibold text-gray-700">{isAr ? 'اسم الحقل' : 'Field Name'}</th>
                    <th className="text-left px-5 py-3 font-semibold text-gray-700">{isAr ? 'النوع' : 'Type'}</th>
                    <th className="text-left px-5 py-3 font-semibold text-gray-700">{isAr ? 'مطلوب' : 'Required'}</th>
                    <th className="text-left px-5 py-3 font-semibold text-gray-700">{isAr ? 'التحقق' : 'Validation'}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {fields.map((field, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-5 py-3 font-mono text-xs text-blue-700 font-semibold">{field.field}</td>
                      <td className="px-5 py-3 text-gray-600 capitalize">{field.type}</td>
                      <td className="px-5 py-3">
                        {field.required
                          ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full"><CheckCircle className="w-3 h-3" /> {isAr ? 'نعم' : 'Yes'}</span>
                          : <span className="text-xs text-gray-400">{isAr ? 'لا' : 'No'}</span>}
                      </td>
                      <td className="px-5 py-3 text-gray-500 text-xs">{field.validation || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-6 py-8 text-center text-gray-400 text-sm">
              {isAr ? 'لا توجد حقول إلزامية محددة لهذه المرحلة' : 'No mandatory fields defined for this stage yet'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────

const NAV_TABS: { id: ActiveSection; labelEn: string; labelAr: string; icon: React.ElementType }[] = [
  { id: 'overview', labelEn: 'Overview', labelAr: 'نظرة عامة', icon: Settings },
  { id: 'stagemap', labelEn: 'Stage Map', labelAr: 'خريطة المراحل', icon: GitBranch },
  { id: 'automations', labelEn: 'Automations', labelAr: 'الأتمتة', icon: Zap },
  { id: 'fields', labelEn: 'Field Config', labelAr: 'إعداد الحقول', icon: Database },
  { id: 'validation', labelEn: 'Validation', labelAr: 'التحقق', icon: Shield },
  { id: 'integrations', labelEn: 'Integrations', labelAr: 'التكاملات', icon: ArrowRight },
];

export default function CRMConfiguration() {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const [activeSection, setActiveSection] = useState<ActiveSection>('overview');

  const stages = getAllStages();
  const crmStages = getCRMStages();
  const totalAutomations = Object.values(STAGE_AUTOMATIONS).reduce((sum, a) => sum + a.length, 0);
  const totalRules = Object.values(VALIDATION_RULES).reduce((sum, r) => sum + r.length, 0);
  const totalFields = stages.reduce((sum, s) => sum + (s.mandatoryFields?.length ?? 0), 0);

  const overviewStats = [
    { labelEn: 'Pipeline Stages', labelAr: 'مراحل المنظومة', value: stages.length, icon: Layers, color: 'text-blue-600' },
    { labelEn: 'CRM Stages', labelAr: 'مراحل CRM', value: crmStages.length, icon: GitBranch, color: 'text-purple-600' },
    { labelEn: 'Total Automations', labelAr: 'إجمالي الأتمتة', value: totalAutomations, icon: Zap, color: 'text-orange-600' },
    { labelEn: 'Mandatory Fields', labelAr: 'الحقول الإلزامية', value: totalFields, icon: Database, color: 'text-green-600' },
    { labelEn: 'Validation Rules', labelAr: 'قواعد التحقق', value: totalRules, icon: Shield, color: 'text-red-600' },
    { labelEn: 'Integrations', labelAr: 'التكاملات', value: INTEGRATIONS.length, icon: ArrowRight, color: 'text-teal-600' },
  ];

  const setupPrinciples = [
    { en: 'One CRM record per unique lead (mobile + active opportunity)', ar: 'سجل CRM واحد لكل عميل فريد (جوال + فرصة نشطة)', icon: '🔒' },
    { en: 'Every stage transition is timestamped and auditable', ar: 'كل انتقال مرحلة مؤرّخ وقابل للمراجعة', icon: '📋' },
    { en: 'SLA timers fire automatically on stage entry', ar: 'مؤقتات الاتفاقية تعمل تلقائيًا عند دخول المرحلة', icon: '⏱️' },
    { en: 'Automation triggers on event, not on schedule', ar: 'محفزات الأتمتة على الحدث، ليس على الجدول', icon: '⚡' },
    { en: 'All outbound comms logged back to CRM record', ar: 'جميع الاتصالات الصادرة مسجلة في سجل CRM', icon: '💬' },
    { en: 'Role-based field visibility enforced by profile', ar: 'رؤية الحقول مبنية على الدور ومفروضة بواسطة الملف الشخصي', icon: '👤' },
  ];

  return (
    <div className={`max-w-7xl mx-auto px-4 py-8 ${isAr ? 'rtl' : 'ltr'}`} dir={isAr ? 'rtl' : 'ltr'}>

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-3 bg-violet-600 rounded-xl">
            <Settings className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {isAr ? 'إعداد CRM' : 'CRM Configuration'}
            </h1>
            <p className="text-gray-500 text-sm mt-0.5">
              {isAr ? 'المواصفات التقنية وقواعد الأتمتة وإعداد الحقول وخريطة التكاملات' : 'Technical specifications, automation rules, field config, and integration map'}
            </p>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {overviewStats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.labelEn} className="bg-white rounded-xl border border-gray-200 p-4 text-center shadow-sm">
              <Icon className={`w-5 h-5 ${stat.color} mx-auto mb-2`} />
              <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
              <div className="text-xs text-gray-500 mt-0.5 leading-tight">{isAr ? stat.labelAr : stat.labelEn}</div>
            </div>
          );
        })}
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl mb-6 overflow-x-auto">
        {NAV_TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveSection(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                activeSection === tab.id
                  ? 'bg-white text-violet-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <Icon className="w-4 h-4" />
              {isAr ? tab.labelAr : tab.labelEn}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeSection === 'overview' && (
        <div className="space-y-6">
          {/* Setup Principles */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5 text-violet-600" />
              {isAr ? 'مبادئ إعداد CRM' : 'CRM Configuration Principles'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {setupPrinciples.map((p, i) => (
                <div key={i} className="flex items-start gap-3 p-3.5 bg-violet-50 rounded-xl border border-violet-100">
                  <span className="text-xl flex-shrink-0">{p.icon}</span>
                  <span className="text-sm text-gray-700">{isAr ? p.ar : p.en}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Quick links to sections */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {NAV_TABS.filter(t => t.id !== 'overview').map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveSection(tab.id)}
                  className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-200 hover:border-violet-300 hover:bg-violet-50 text-left transition-all group shadow-sm"
                >
                  <div className="p-2.5 bg-violet-100 rounded-lg group-hover:bg-violet-200 transition-colors">
                    <Icon className="w-5 h-5 text-violet-700" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-800">{isAr ? tab.labelAr : tab.labelEn}</div>
                    <div className="text-xs text-gray-500">
                      {tab.id === 'stagemap' && (isAr ? `${stages.length} مراحل` : `${stages.length} stages`)}
                      {tab.id === 'automations' && (isAr ? `${totalAutomations} أتمتة` : `${totalAutomations} automations`)}
                      {tab.id === 'fields' && (isAr ? `${totalFields} حقل` : `${totalFields} total fields`)}
                      {tab.id === 'validation' && (isAr ? `${totalRules} قاعدة` : `${totalRules} rules`)}
                      {tab.id === 'integrations' && (isAr ? `${INTEGRATIONS.length} تكامل` : `${INTEGRATIONS.length} integrations`)}
                    </div>
                  </div>
                  <ChevronRight className={`w-4 h-4 text-gray-400 ml-auto group-hover:text-violet-600 ${isAr ? 'rotate-180' : ''}`} />
                </button>
              );
            })}
          </div>

          {/* Automation by category summary */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-orange-500" />
              {isAr ? 'ملخص الأتمتة حسب الفئة' : 'Automation Summary by Category'}
            </h2>
            <div className="flex flex-wrap gap-2">
              {(() => {
                const catCount: Record<string, number> = {};
                Object.values(STAGE_AUTOMATIONS).flatMap(a => a).forEach(auto => {
                  catCount[auto.category] = (catCount[auto.category] || 0) + 1;
                });
                return Object.entries(catCount).sort((a, b) => b[1] - a[1]).map(([cat, count]) => (
                  <div key={cat} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${CATEGORY_COLORS[cat] || 'bg-gray-100 text-gray-600'}`}>
                    <span className="capitalize">{cat.replace(/_/g, ' ')}</span>
                    <span className="bg-white bg-opacity-60 rounded-full px-1.5 font-bold">{count}</span>
                  </div>
                ));
              })()}
            </div>
          </div>

          {/* Stage quick-links */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-violet-600" />
              {isAr ? 'تفاصيل المرحلة في المنظومة' : 'Stage Details in Pipeline'}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {stages.map((stage) => (
                <Link
                  key={stage.meta.id}
                  to={`/pipeline/stage/${stage.meta.id}`}
                  className="flex items-center gap-2 p-3 rounded-xl border border-gray-200 hover:border-violet-300 hover:bg-violet-50 transition-all group"
                >
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                    style={{ backgroundColor: stage.meta.color || '#6366f1' }}
                  >
                    {stage.meta.order === 0 ? 'P' : stage.meta.order}
                  </div>
                  <span className="text-xs font-medium text-gray-700 group-hover:text-violet-700 truncate">
                    {isAr ? stage.meta.titleAr : stage.meta.title}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeSection === 'stagemap' && <StageMapTab isAr={isAr} />}
      {activeSection === 'automations' && <AutomationsTab isAr={isAr} />}
      {activeSection === 'fields' && <FieldsTab isAr={isAr} />}
      {activeSection === 'validation' && <ValidationTab isAr={isAr} />}
      {activeSection === 'integrations' && <IntegrationsTab isAr={isAr} />}
    </div>
  );
}
